"use client";

import React from "react";
import { LGS } from "modules/lgs/model/lgs";
import DynamicSimpleMap from "modules/lgs/components/stores/map/DynamicSimpleMap";
import {
  ArrowLeftRight,
  Globe,
  MapPin,
  Phone,
  Mail,
  Store,
} from "lucide-react";
import Link from "next/link";
import { ensureValidWebsiteUrl } from "utils/utils";
import {
  createCityUrl,
  nameToSlug,
} from "modules/common/components/map/utils/formatCityName";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { LgsLogo } from "modules/lgs/components/LgsLogo";

interface LGSStoreSidebarProps {
  lgs: LGS;
  cityName?: string | null;
}

export function LGSStoreSidebar({ lgs, cityName }: LGSStoreSidebarProps) {
  const validWebsiteUrl = ensureValidWebsiteUrl(lgs.website);

  const cityLink = React.useMemo(() => {
    if (!cityName) return null;

    const parts = cityName.split(",").map((s) => s.trim());

    if (parts.length >= 2) {
      const city = parts[0];
      const country = parts[parts.length - 1];

      if (!city || !country) return null;

      const citySlug = nameToSlug(city);
      const countrySlug = nameToSlug(country);

      if (!citySlug || !countrySlug) return null;

      return createCityUrl(country, city);
    }

    return null;
  }, [cityName]);

  return (
    <div className="card border-base-content/20 sticky top-20 border">
      <div className="card-body p-4">
        {/* Map */}
        {lgs.latitude && lgs.longitude && (
          <div className="rounded-t-box -m-4 mb-1 h-[250px] overflow-hidden">
            <DynamicSimpleMap store={lgs} />
          </div>
        )}
        {/* City Trading Link */}
        {cityLink && (
          <Link
            href={`${cityLink}/haves`}
            className="btn btn-soft btn-sm btn-block"
            title={`View all trades in ${cityName}`}
          >
            <MapPin size={16} />
            City Trading
          </Link>
        )}
        {/* Store Details */}
        <div className="flex flex-col gap-2">
          {lgs.postalAddress && (
            <div className="text-base-content/70 flex items-start gap-2 text-sm">
              <MapPin size={16} className="mt-0.5 shrink-0" />
              <span>{lgs.postalAddress}</span>
            </div>
          )}

          {validWebsiteUrl && (
            <div className="text-base-content/70 flex items-center gap-2 text-sm">
              <Globe size={16} className="shrink-0" />
              <Link
                href={validWebsiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary truncate hover:underline"
              >
                {lgs.website}
              </Link>
            </div>
          )}

          {lgs.showEmailInSEL && lgs.phoneNumber && (
            <div className="text-base-content/70 flex items-center gap-2 text-sm">
              <Phone size={16} className="shrink-0" />
              <span>{lgs.phoneNumber}</span>
            </div>
          )}

          {lgs.showEmailInSEL && lgs.emailAddress && (
            <div className="text-base-content/70 flex items-center gap-2 text-sm">
              <Mail size={16} className="shrink-0" />
              <span>{lgs.emailAddress}</span>
            </div>
          )}
        </div>
        {/* Divider + CTA */}
        <div className="border-base-content/10 border-t pt-2">
          {/* A hub with no uploaded logo drops the figure once both variants
              exhaust: the fallback is an <svg>, only real logos are <img>. */}
          {lgs.isTradingHub && (
            <figure className="p-4 [&:not(:has(img))]:hidden">
              <LgsLogo
                wotcId={lgs.wotcId}
                storeName={lgs.name}
                isTradingHub
                size="banner"
                className="block w-full"
                priority
              />
            </figure>
          )}
          {lgs.isTradingHub ? (
            <div className="rounded-box flex items-start gap-2">
              <ArrowLeftRight
                size={ICON_SIZE}
                className="text-success mt-0.5 shrink-0"
              />
              <div className="flex flex-col gap-1">
                <p className="text-base-content text-sm font-medium">
                  Trading Hub
                </p>
                <p className="text-base-content/60 text-xs">
                  {lgs.name} welcomes in-person trades. Browse cards from
                  players who trade here!
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-box flex items-start gap-2">
              <Store
                size={ICON_SIZE}
                className="text-base-content/50 mt-0.5 shrink-0"
              />
              <div className="flex flex-col gap-1">
                <p className="text-base-content text-sm font-medium">
                  Is this your store?
                </p>
                <p className="text-base-content/60 text-xs">
                  Claim this page to manage your listing and connect with
                  traders.
                </p>
                <Link
                  href="/contact"
                  className="text-secondary mt-1 text-xs hover:underline"
                >
                  Claim page &rarr;
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
